import type {
  BrandConcessionContentDM,
  ProductSetContentDM,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/MultiSectionView.types';
import type { FloorContentDM, FloorLayout } from '@Model/FloorModels';
import { ObservableProp } from '@Utils/useClassStore';
import { inject, injectable } from 'inversify';
import { computed } from 'mobx';

import { BrandConcessionsAPI } from '~gateways/brand-concession';
import { ProductSetsAPI } from '~gateways/product-set';
import container from '~ioc/ioc';
import { FloorsListStore } from '~stores/floors-list';
import type { JourneyParameters } from '~types/navigation';

@injectable()
export class BbContentData {
  @inject(BrandConcessionsAPI)
  private brandConcessionsAPI!: BrandConcessionsAPI;
  @inject(ProductSetsAPI)
  private productSetsAPI!: ProductSetsAPI;

  @inject(FloorsListStore) private floorsStore!: FloorsListStore;

  journeyParams = new ObservableProp<JourneyParameters | undefined>(undefined);
  isFetching = new ObservableProp(false);

  data = new ObservableProp<
    BrandConcessionContentDM | FloorContentDM | ProductSetContentDM | undefined
  >(undefined);
  dataType = new ObservableProp<
    | 'BrandConcessionContentDM'
    | 'FloorContentDM'
    | 'ProductSetContentDM'
    | undefined
  >(undefined);

  // If it is a base floor content then floor layout depends on the floor content
  @computed get floorLayout(): FloorLayout | undefined {
    // if data is not of type FloorContentDM then layout will be undefined
    return (this.data.value as FloorContentDM | undefined)?.layout;
  }

  error = new ObservableProp(false);
  brandConcessionError = new ObservableProp('');

  init = (journeyParams: JourneyParameters) => {
    this.data.set(undefined);
    this.journeyParams.set(journeyParams);
    this.fetch().then();
  };

  fetch = async () => {
    const journey = this.journeyParams.value?.journey;

    try {
      this.isFetching.set(true);

      if (journey === 'floor') {
        // get data from 'floors' area of the app
        const floorId = Number(this.journeyParams.value?.journeyParam);
        const floorStore = this.floorsStore.getFloorStore(floorId);

        await floorStore.fetch();
        const data = floorStore.content.value;
        this.data.set(data ?? undefined);
        this.dataType.set(data ? 'FloorContentDM' : undefined);
      } else {
        // get data from 'journeys' area of the app
        const data = await this.fetchJourney();
        this.data.set(data.data);
        this.dataType.set(data.type);
      }
    } catch (error) {
      this.error.set(false);

      if (journey === 'brandconcession' || journey === 'productset') {
        this.brandConcessionError.set((error as Error).message);
      }
    } finally {
      this.isFetching.set(false);
    }
  };

  @computed get fetchError(): string | undefined {
    const journey = this.journeyParams.value?.journey;

    if (journey === 'floor') {
      const floorId = Number(this.journeyParams.value?.journeyParam);
      if (!floorId) {
        return undefined;
      }
      // Don't try to get floorId with floorStore because it will try to fetch the floor
      const floorStore = this.floorsStore.getFloorStore(floorId);

      return floorStore.fetchError.value;
    } else if (journey === 'brandconcession' || journey === 'productset') {
      return this.brandConcessionError.value;
    }

    return undefined;
  }

  private fetchJourney = async (): Promise<{
    data: BrandConcessionContentDM | ProductSetContentDM;
    type: 'BrandConcessionContentDM' | 'ProductSetContentDM';
  }> => {
    const { journey, journeyParam = 0 } = this.journeyParams.value!;

    if (journey === 'brandconcession') {
      const data = (await this.brandConcessionsAPI.get(
        journeyParam
      )) as BrandConcessionContentDM;
      return { data, type: 'BrandConcessionContentDM' };
    }

    if (journey === 'productset') {
      const data = (await this.productSetsAPI.get(
        journeyParam
      )) as ProductSetContentDM;

      return {
        data: this.convertPStoBC(data),
        type: 'BrandConcessionContentDM',
      };
    }

    throw new Error(`Unknown journey type to fetch content ${journey}`);
  };

  private convertPStoBC = (
    productSet: ProductSetContentDM
  ): BrandConcessionContentDM => {
    const {
      productSetId,
      departmentId,
      departmentName,
      floorId,
      floorName,
      title,
      text,
      heroImageUrl,
    } = productSet;

    return {
      concessionId: productSetId,
      // TODO: Api needs to send brand info
      brandId: productSetId,
      brandName: '',
      departmentId,
      departmentName,
      floorId,
      floorName,
      header: {
        // TODO: Api needs to send Header info
        title,
        text,
        heroImageUrl,
        backgroundMode: 'None',
        fontColour: 'black',
        backgroundColour: 'white',
        backgroundImageUrl: '',
      },
      sections: [],
      jumps: [],
    };
  };
}

container.bind(BbContentData).toSelf().inTransientScope();
